/**
 * Featured Collection Section JavaScript
 * Handles carousel functionality, product interactions, and responsive behavior
 */

class FeaturedCollectionCarousel {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.carousel = document.getElementById(`carousel-${sectionId}`);
    this.indicators = document.getElementById(`indicators-${sectionId}`);
    this.currentIndex = 0;
    this.itemWidth = 296; // 280px + 16px gap
    this.itemsVisible = this.calculateVisibleItems();
    this.totalItems = this.carousel ? this.carousel.children.length : 0;
    this.maxIndex = Math.max(0, this.totalItems - this.itemsVisible);
    this.isScrolling = false;
    this.touchStartX = 0;
    this.touchEndX = 0;
    
    this.init();
  }

  init() {
    if (!this.carousel) return;
    
    this.createIndicators();
    this.setupEventListeners();
    this.setupTouchEvents();
    this.setupIntersectionObserver();
    this.updateCarouselPosition();
    
    // Auto-scroll functionality (optional)
    if (this.shouldAutoScroll()) {
      this.startAutoScroll();
    }
  }

  calculateVisibleItems() {
    if (!this.carousel) return 4;
    
    // Get the actual carousel container width
    const carouselContainer = this.carousel.parentElement;
    const containerWidth = carouselContainer.offsetWidth;
    
    // Account for padding and gaps
    const availableWidth = containerWidth - 40; // 40px total padding
    const visibleItems = Math.floor(availableWidth / this.itemWidth);
    
    // Ensure at least 1 item is visible, but not more than total items
    return Math.max(1, Math.min(visibleItems, this.totalItems));
  }

  createIndicators() {
    if (!this.indicators || this.totalItems <= this.itemsVisible) return;
    
    this.indicators.innerHTML = '';
    const indicatorCount = this.maxIndex + 1;
    
    for (let i = 0; i <= this.maxIndex; i++) {
      const indicator = document.createElement('button');
      indicator.className = `carousel-indicator ${i === 0 ? 'active' : ''}`;
      indicator.setAttribute('aria-label', `Go to slide ${i + 1}`);
      indicator.addEventListener('click', () => this.goToSlide(i));
      this.indicators.appendChild(indicator);
    }
  }

  setupEventListeners() {
    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.target.closest(`#carousel-${this.sectionId}`)) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prevSlide();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.nextSlide();
        }
      }
    });

    // Resize handler with debouncing for better performance
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Recalculate based on new container dimensions
        this.itemsVisible = this.calculateVisibleItems();
        this.maxIndex = Math.max(0, this.totalItems - this.itemsVisible);
        this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
        
        // Update carousel position and indicators
        this.updateCarouselPosition();
        this.createIndicators();
        
        // Update item width based on new container size
        this.updateItemWidth();
      }, 250);
    });

    // Pause auto-scroll on hover
    this.carousel.addEventListener('mouseenter', () => this.pauseAutoScroll());
    this.carousel.addEventListener('mouseleave', () => this.resumeAutoScroll());
  }

  setupTouchEvents() {
    if (!this.carousel) return;

    this.carousel.addEventListener('touchstart', (e) => {
      this.touchStartX = e.touches[0].clientX;
    }, { passive: true });

    this.carousel.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].clientX;
      this.handleSwipe();
    }, { passive: true });
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const swipeDistance = this.touchStartX - this.touchEndX;

    if (Math.abs(swipeDistance) > swipeThreshold) {
      if (swipeDistance > 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    }
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.lazyLoadImages(entry.target);
        }
      });
    }, options);

    // Observe all product images
    this.carousel.querySelectorAll('.product-image[data-src]').forEach(img => {
      observer.observe(img);
    });
  }

  lazyLoadImages(container) {
    const images = container.querySelectorAll('.product-image[data-src]');
    images.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        img.setAttribute('data-loading', 'true');
        
        img.addEventListener('load', () => {
          img.removeAttribute('data-loading');
        });
      }
    });
  }

  nextSlide() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0; // Loop back to start
    }
    this.updateCarouselPosition();
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      this.currentIndex = this.maxIndex; // Loop to end
    }
    this.updateCarouselPosition();
  }

  goToSlide(index) {
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    this.updateCarouselPosition();
  }

  updateCarouselPosition() {
    if (!this.carousel || this.isScrolling) return;

    this.isScrolling = true;
    const scrollLeft = this.currentIndex * this.itemWidth;
    
    // Smooth scroll to position
    this.carousel.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });

    this.updateIndicators();
    
    // Reset scrolling flag after animation
    setTimeout(() => {
      this.isScrolling = false;
    }, 300);
  }

  updateIndicators() {
    if (!this.indicators) return;
    
    const indicators = this.indicators.querySelectorAll('.carousel-indicator');
    indicators.forEach((indicator, index) => {
      indicator.classList.toggle('active', index === this.currentIndex);
    });
  }

  shouldAutoScroll() {
    return this.totalItems > this.itemsVisible && window.innerWidth > 768;
  }

  startAutoScroll() {
    this.autoScrollInterval = setInterval(() => {
      if (!this.isPaused) {
        this.nextSlide();
      }
    }, 4000); // 4 seconds
  }

  pauseAutoScroll() {
    this.isPaused = true;
  }

  resumeAutoScroll() {
    this.isPaused = false;
  }

  updateItemWidth() {
    if (!this.carousel) return;
    
    const carouselContainer = this.carousel.parentElement;
    const containerWidth = carouselContainer.offsetWidth;
    const items = this.carousel.querySelectorAll('.carousel-item');
    
    if (items.length > 0 && containerWidth > 0) {
      // Calculate optimal item width based on container size
      const gap = 16; // Gap between items
      const padding = 40; // Total padding
      const availableWidth = containerWidth - padding;
      const optimalItemWidth = (availableWidth - (gap * (this.itemsVisible - 1))) / this.itemsVisible;
      
      // Update item width if it's reasonable
      if (optimalItemWidth > 180 && optimalItemWidth < 400) {
        this.itemWidth = optimalItemWidth + gap;
        
        // Apply the new width to items
        items.forEach(item => {
          item.style.width = `${optimalItemWidth}px`;
        });
      }
    }
  }

  destroy() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
    }
  }
}

// Global carousel instances
const carouselInstances = new Map();

// Global functions for Liquid template
window.slideCarousel = function(sectionId, direction) {
  const carousel = carouselInstances.get(sectionId);
  if (carousel) {
    if (direction === 'next') {
      carousel.nextSlide();
    } else {
      carousel.prevSlide();
    }
  }
};

window.quickAddToCart = async function(variantId) {
  try {
    // Show loading state
    const button = event.target;
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;

    const formData = {
      'items': [{
        'id': variantId,
        'quantity': 1
      }]
    };

    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    });

    if (response.ok) {
      // Update cart count if function exists
      if (window.updateCartCount) {
        window.updateCartCount();
      }
      
      // Show success feedback
      button.textContent = 'Added!';
      button.style.background = '#28a745';
      
      // Trigger cart drawer or notification
      if (window.openCartDrawer) {
        window.openCartDrawer();
      } else {
        showNotification('Product added to cart!');
      }
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
        button.disabled = false;
      }, 2000);
    } else {
      throw new Error('Failed to add to cart');
    }
  } catch (error) {
    console.error('Error adding to cart:', error);
    button.textContent = 'Error';
    button.style.background = '#dc3545';
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.background = '';
      button.disabled = false;
    }, 2000);
  }
};

function showNotification(message) {
  // Create notification element
  const notification = document.createElement('div');
  notification.className = 'cart-notification';
  notification.textContent = message;
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #28a745;
    color: white;
    padding: 12px 20px;
    border-radius: 6px;
    z-index: 10000;
    animation: slideInRight 0.3s ease;
  `;

  document.body.appendChild(notification);

  setTimeout(() => {
    notification.style.animation = 'slideOutRight 0.3s ease';
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 300);
  }, 3000);
}

// Initialize carousels when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Find all featured collection sections
  const sections = document.querySelectorAll('.featured-collection-section');
  
  sections.forEach(section => {
    const sectionId = section.querySelector('[id^="carousel-"]')?.id?.replace('carousel-', '');
    if (sectionId) {
      const carousel = new FeaturedCollectionCarousel(sectionId);
      carouselInstances.set(sectionId, carousel);
    }
  });
});

// Clean up on page unload
window.addEventListener('beforeunload', function() {
  carouselInstances.forEach(carousel => carousel.destroy());
  carouselInstances.clear();
});

// Add CSS animations for notifications
if (!document.querySelector('#carousel-animations')) {
  const style = document.createElement('style');
  style.id = 'carousel-animations';
  style.textContent = `
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
    
    @keyframes slideOutRight {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }
    
    .cart-notification {
      font-family: inherit;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  `;
  document.head.appendChild(style);
}

// Export for module systems if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FeaturedCollectionCarousel;
}